import React, { useState, useEffect, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  Badge,
  ProgressBar,
  Spinner,
  Nav,
  Pagination,
  Table,
  Form,
} from "react-bootstrap";
import {
  getUserById,
  sendPasswordResetEmail,
  getUserPracticeById,
  getStatementRuns,
  getStatementRun,
  getStatementRunItems,
  cancelStatementRun,
  retryFailedItems,
} from "../api";
import { EditUser } from "../Components/Users/EditUser";
import PreferencesPanel from "../Components/BootNav/PreferencesPanel";
import { jwtDecode } from "jwt-decode";
import { useLocation } from "react-router-dom";
import { UserAvailability } from "../Components/Users/Availability";
import { UserProfilePic } from "../Components/Users/UserProfilePic";
import { getSupportRequests } from "../atasksapi";
import ReleaseNotesSection from "../Components/Releases/ReleaseNotesSection";
import { DeliveryStatusCell } from "../Components/BulkStatements/DeliveryStatusCell";

const STATUS_BADGES = {
  QUEUED: "secondary",
  RUNNING: "primary",
  COMPLETED: "success",
  COMPLETED_WITH_ERRORS: "warning",
  CANCELLED: "danger",
};

const ITEM_STATUS_BADGES = {
  QUEUED: "secondary",
  GENERATING: "info",
  GENERATED: "primary",
  EMAILED: "success",
  FAILED: "danger",
  SKIPPED: "warning",
};

export function UserProfile({ onPPicUpdate }) {
  const location = useLocation();
  const [refresh, setRefresh] = useState(false);

  const [user, setUser] = useState({
    email: "",
    firstName: "",
    lastName: "",
    occupation: "",
    role: "",
    practice: [],
  });

  // Parse query params for deep linking from notifications
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get("tab") || "profile";
  const initialRunId = searchParams.get("runId") || null;

  const [activeSection, setActiveSection] = useState(initialTab);

  // Tasks state
  const [taskRuns, setTaskRuns] = useState([]);
  const [taskRunsLoading, setTaskRunsLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(initialRunId);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunLoading, setSelectedRunLoading] = useState(false);
  const [taskDetailTab, setTaskDetailTab] = useState("status");
  // Items state
  const [runItems, setRunItems] = useState([]);
  const [runItemsTotal, setRunItemsTotal] = useState(0);
  const [runItemsPage, setRunItemsPage] = useState(1);
  const [runItemsFilter, setRunItemsFilter] = useState("");
  const [runItemsLoading, setRunItemsLoading] = useState(false);
  const [expandedError, setExpandedError] = useState(null);
  const ITEMS_PER_PAGE = 50;

  // Support requests state
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);

  // Fetch runs list
  const fetchTaskRuns = useCallback(async () => {
    setTaskRunsLoading(true);
    try {
      const data = await getStatementRuns(1, 50);
      setTaskRuns(data.runs || []);
    } catch (err) {
      console.error("Error fetching statement runs:", err);
    } finally {
      setTaskRunsLoading(false);
    }
  }, []);

  // Fetch single run detail
  const fetchSelectedRun = useCallback(async () => {
    if (!selectedRunId) return;
    setSelectedRunLoading(true);
    try {
      const data = await getStatementRun(selectedRunId);
      setSelectedRun(data);
    } catch (err) {
      console.error("Error fetching run:", err);
    } finally {
      setSelectedRunLoading(false);
    }
  }, [selectedRunId]);

  // Fetch run items
  const fetchRunItems = useCallback(async () => {
    if (!selectedRunId) return;
    setRunItemsLoading(true);
    try {
      const params = { page: runItemsPage, limit: ITEMS_PER_PAGE };
      if (runItemsFilter) params.status = runItemsFilter;
      const data = await getStatementRunItems(selectedRunId, params);
      setRunItems(data.items || []);
      setRunItemsTotal(data.total || 0);
    } catch (err) {
      console.error("Error fetching run items:", err);
    } finally {
      setRunItemsLoading(false);
    }
  }, [selectedRunId, runItemsPage, runItemsFilter]);

  // Load runs when tasks tab is active
  useEffect(() => {
    if (activeSection === "tasks") {
      fetchTaskRuns();
    }
  }, [activeSection, fetchTaskRuns]);

  // Load run detail when selected
  useEffect(() => {
    if (selectedRunId) {
      fetchSelectedRun();
    }
  }, [selectedRunId, fetchSelectedRun]);

  // Poll active runs
  useEffect(() => {
    if (!selectedRun) return;
    if (selectedRun.status === "RUNNING" || selectedRun.status === "QUEUED") {
      const interval = setInterval(fetchSelectedRun, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedRun, fetchSelectedRun]);

  // Fetch items when on items tab
  useEffect(() => {
    if (taskDetailTab === "items" && selectedRunId) {
      fetchRunItems();
    }
  }, [taskDetailTab, selectedRunId, fetchRunItems]);

  // Deep link: if URL has runId, auto-select it
  useEffect(() => {
    if (initialRunId && activeSection === "tasks") {
      setSelectedRunId(initialRunId);
    }
  }, [initialRunId, activeSection]);

  // Fetch support requests when support tab is active
  const fetchSupportRequests = useCallback(async () => {
    setSupportLoading(true);
    try {
      const data = await getSupportRequests();
      setSupportRequests(data.requests || []);
    } catch (err) {
      console.error("Error fetching support requests:", err);
    } finally {
      setSupportLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === "support") {
      fetchSupportRequests();
    }
  }, [activeSection, fetchSupportRequests]);
  const [ppicModalShow, setPpicModalShow] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPPicURL, setSelectedPPicURL] = useState(null);
  const [passwordResetModal, setPasswordResetModal] = useState(false);
  const [practice, setPractice] = useState([]);
  const [modalShow, setModalShow] = useState(false);

  const handleRefresh = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    async function fetchUserData() {
      try {
        const token = sessionStorage.getItem("user");
        if (token) {
          const decoded = jwtDecode(token);
          const userData = await getUserById(decoded._id);
          if (userData) {
            setUser(userData);
          }
          const practiceData = await getUserPracticeById(decoded._id);
          if (practiceData) {
            setPractice(practiceData);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    }
    fetchUserData();
  }, [refresh]);

  const handlePPicClick = (userId, userPPicURL) => {
    setSelectedUserId(userId); // Set the practice to be edited
    setSelectedPPicURL(userPPicURL);
    setPpicModalShow(true); // Show the modal
  };

  const handleEditClick = (userId) => {
    setModalShow(true);
  };

  async function handleResetPassword(user) {
    setPasswordResetModal(true); // ✅ Show modal immediately

    try {
      await sendPasswordResetEmail(user.email); // ✅ Send password reset email
      console.log(`✅ Password reset email sent to ${user.email}`);
    } catch (error) {
      console.error("⛔ Error sending password reset email:", error);
    }
  }

  function renderProfileInformation() {
    return (
      <>
        <Container className="container pt-0 bg-white shadow-sm pb-3 mb-3 w-100">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="text-primary mb-1 fw-normal">Profile Information</h5>
            <Button variant="" onClick={() => handleEditClick(user._id)}>
              <i className="fas fa-edit text-primary"></i>
            </Button>
          </div>

          <table className="w-100 mt-3" style={{ borderCollapse: "collapse" }}>
            <tbody>
              {[
                { label: "Email:", value: user.email },
                { label: "First Name:", value: user.firstName },
                { label: "Last Name:", value: user.lastName },
                { label: "Occupation:", value: user.occupation },
                {
                  label: "System Role:",
                  value:
                    user.role.length > 0
                      ? user.role.map((r, index) => <div key={index}>{r}</div>)
                      : "None",
                },
                {
                  label: "Practices:",
                  value:
                    practice.length > 0
                      ? practice.map((p, index) => (
                          <div key={index}>{p.practiceName}</div>
                        ))
                      : "None",
                },
                {
                  label: "Reset Password:",
                  value: (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-flex align-items-center"
                      onClick={() => handleResetPassword(user)}
                    >
                      <i className="fas fa-key me-2 text-primary"></i>
                      Reset
                    </Button>
                  ),
                },
              ].map((row, index) => (
                <tr key={index} style={{ verticalAlign: "top" }}>
                  <td className="text-primary fw-bold text-end pe-3 pb-2">
                    {row.label}
                  </td>
                  <td className="pb-2">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Container>

        {/* Preferences — UI defaults that flow into the JWT, persisted via
            POST /users/me/preferences. Styled to mirror the Profile card. */}
        <Container className="container pt-3 bg-white shadow-sm pb-3 mb-3 w-100">
          <h5 className="text-primary mb-3 fw-normal">Preferences</h5>
          <PreferencesPanel onSaved={handleRefresh} />
        </Container>

        {passwordResetModal && (
          <PasswordResetModal
            show={passwordResetModal}
            onHide={() => setPasswordResetModal(false)}
            user={user}
          />
        )}

        {modalShow && (
          <EditUser
            show={modalShow}
            onHide={() => setModalShow(false)}
            userId={user._id}
            onUserUpdate={handleRefresh}
          />
        )}
      </>
    );
  }

  function PasswordResetModal({ show, onHide, user }) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header>
          <Modal.Title>Password Reset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The password reset request for{" "}
            <strong>
              {user?.firstName} {user?.lastName}
            </strong>{" "}
            has been initiated.
          </p>
          <p>
            An email will be sent shortly with instructions to reset the
            password.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  function handleRunCancel(runId) {
    if (!window.confirm("Are you sure you want to cancel this run?")) return;
    cancelStatementRun(runId).then(() => fetchSelectedRun()).catch(console.error);
  }

  function handleRunRetry(runId) {
    retryFailedItems(runId).then(() => fetchSelectedRun()).catch(console.error);
  }

  function renderRunList() {
    if (taskRunsLoading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" /> Loading runs...
        </div>
      );
    }
    if (taskRuns.length === 0) {
      return <p className="text-muted text-center py-4">No statement runs found.</p>;
    }
    return (
      <div className="table-responsive">
        <Table bordered hover responsive size="sm">
          <thead className="table-primary text-center">
            <tr>
              <th>Month</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Emailed</th>
              <th>Failed</th>
              <th>Started By</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {taskRuns.map((r) => {
              const total = r.counts?.eligible || 0;
              const processed =
                (r.counts?.emailed || 0) +
                (r.counts?.failed || 0) +
                (r.counts?.skipped || 0);
              return (
                <tr key={r._id} style={{ cursor: "pointer" }} onClick={() => { setSelectedRunId(r._id); setTaskDetailTab("status"); }}>
                  <td>{r.month}</td>
                  <td>
                    <Badge bg={STATUS_BADGES[r.status] || "secondary"}>
                      {r.status?.replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td>{processed}/{total}</td>
                  <td>{r.counts?.emailed || 0}</td>
                  <td>
                    {r.counts?.failed > 0 ? (
                      <span className="text-danger fw-bold">{r.counts.failed}</span>
                    ) : 0}
                  </td>
                  <td>{r.startedBy?.userName || "Unknown"}</td>
                  <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</td>
                  <td>
                    <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedRunId(r._id); setTaskDetailTab("status"); }}>
                      View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  function renderRunDetail() {
    if (selectedRunLoading && !selectedRun) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      );
    }
    if (!selectedRun) return null;

    const counts = selectedRun.currentCounts || selectedRun.counts || {};
    const totalItems = selectedRun.counts?.eligible || 0;
    const processed =
      (counts.emailed || 0) +
      (counts.failed || 0) +
      (counts.skipped || 0);
    const progressPercent = totalItems > 0 ? Math.round((processed / totalItems) * 100) : 0;
    const isActive = selectedRun.status === "RUNNING" || selectedRun.status === "QUEUED";

    return (
      <div>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0 d-inline me-2">
              Statement Run - {selectedRun.month}
            </h6>
            <Badge bg={STATUS_BADGES[selectedRun.status] || "secondary"}>
              {selectedRun.status?.replace(/_/g, " ")}
            </Badge>
            {selectedRun.settings?.dryRun && (
              <Badge bg="info" className="ms-1">DRY RUN</Badge>
            )}
          </div>
          <Button variant="outline-secondary" size="sm" onClick={() => { setSelectedRunId(null); setSelectedRun(null); }}>
            <i className="fas fa-arrow-left me-1"></i> Back to list
          </Button>
        </div>

        {/* Tabs */}
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link active={taskDetailTab === "status"} onClick={() => setTaskDetailTab("status")} style={{ cursor: "pointer" }}>
              Status
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link active={taskDetailTab === "items"} onClick={() => setTaskDetailTab("items")} style={{ cursor: "pointer" }}>
              Items
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Status Tab */}
        {taskDetailTab === "status" && (
          <div>
            {/* Progress Bar */}
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1 small">
                <span>Progress: {processed} / {totalItems}</span>
                <span>{progressPercent}%</span>
              </div>
              <ProgressBar
                now={progressPercent}
                variant={
                  selectedRun.status === "COMPLETED" ? "success"
                    : selectedRun.status === "COMPLETED_WITH_ERRORS" ? "warning"
                    : "primary"
                }
                animated={isActive}
              />
            </div>

            {/* Stat Cards */}
            <Row className="mb-3">
              {[
                { label: "Eligible", value: totalItems, color: "" },
                { label: "Generated", value: counts.generated || 0, color: "text-info" },
                { label: "Emailed", value: counts.emailed || 0, color: "text-success" },
                { label: "Failed", value: counts.failed || 0, color: "text-danger" },
                { label: "Skipped", value: counts.skipped || 0, color: "text-warning" },
              ].map((stat) => (
                <Col key={stat.label}>
                  <div
                    className="text-center p-2 border rounded"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setTaskDetailTab("items");
                      if (stat.label !== "Eligible") {
                        setRunItemsFilter(stat.label.toUpperCase());
                      } else {
                        setRunItemsFilter("");
                      }
                      setRunItemsPage(1);
                    }}
                  >
                    <div className="small text-muted">{stat.label}</div>
                    <div className={`fw-bold ${stat.color}`}>{stat.value}</div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* Info */}
            <div className="small text-muted mb-3">
              <div>Started by: <strong>{selectedRun.startedBy?.userName || "Unknown"}</strong></div>
              {selectedRun.startedAt && <div>Started: {new Date(selectedRun.startedAt).toLocaleString()}</div>}
              {selectedRun.finishedAt && <div>Finished: {new Date(selectedRun.finishedAt).toLocaleString()}</div>}
              <div>Practices: {selectedRun.practiceNumbers?.join(", ")}</div>
            </div>

            {/* Actions */}
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={() => { setTaskDetailTab("items"); setRunItemsFilter(""); setRunItemsPage(1); }}>
                View All Items
              </Button>
              {isActive && (
                <Button variant="outline-danger" size="sm" onClick={() => handleRunCancel(selectedRunId)}>
                  Cancel Run
                </Button>
              )}
              {(selectedRun.status === "COMPLETED_WITH_ERRORS" || (selectedRun.status === "COMPLETED" && (counts.failed || 0) > 0)) && (
                <Button variant="outline-warning" size="sm" onClick={() => handleRunRetry(selectedRunId)}>
                  Retry Failed ({counts.failed || 0})
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Items Tab */}
        {taskDetailTab === "items" && renderRunItemsTab()}
      </div>
    );
  }

  function renderRunItemsTab() {
    const totalPages = Math.ceil(runItemsTotal / ITEMS_PER_PAGE);

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <Badge bg="secondary">{runItemsTotal} items</Badge>
          </div>
          <Form.Select
            size="sm"
            style={{ width: 150 }}
            value={runItemsFilter}
            onChange={(e) => { setRunItemsFilter(e.target.value); setRunItemsPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="QUEUED">Queued</option>
            <option value="GENERATING">Generating</option>
            <option value="GENERATED">Generated</option>
            <option value="EMAILED">Emailed</option>
            <option value="FAILED">Failed</option>
            <option value="SKIPPED">Skipped</option>
          </Form.Select>
        </div>

        <div className="table-responsive">
          <Table bordered hover responsive size="sm" className="mb-0">
            <thead className="table-primary text-center">
              <tr>
                <th>Account #</th>
                <th>Account Name</th>
                <th className="text-end">Outstanding</th>
                <th>Status</th>
                <th>Delivery</th>
                <th>Email Targets</th>
                <th>Attempts</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {runItemsLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </td>
                </tr>
              ) : runItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">No items found.</td>
                </tr>
              ) : (
                runItems.map((item) => (
                  <React.Fragment key={item._id}>
                    <tr>
                      <td>{item.accountNumber}</td>
                      <td>{item.accountName}</td>
                      <td className="text-end">R {(item.outstandingAtRun || 0).toFixed(2)}</td>
                      <td>
                        <Badge bg={ITEM_STATUS_BADGES[item.status] || "secondary"}>
                          {item.status}
                        </Badge>
                      </td>
                      <td>
                        <DeliveryStatusCell item={item} />
                      </td>
                      <td className="text-truncate" style={{ maxWidth: 200 }} title={item.emailTargets?.join(", ")}>
                        {item.emailTargets?.join(", ") || <span className="text-danger small">None</span>}
                      </td>
                      <td>{item.attempts || 0}</td>
                      <td>
                        {item.errorCode ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => setExpandedError(expandedError === item._id ? null : item._id)}
                          >
                            {item.errorCode}
                          </Button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                    {expandedError === item._id && item.errorMessage && (
                      <tr>
                        <td colSpan={8} className="bg-light">
                          <div className="small text-danger p-2">
                            <strong>Error:</strong> {item.errorMessage}
                            {item.lastAttemptAt && (
                              <div className="mt-1 text-muted">
                                Last attempt: {new Date(item.lastAttemptAt).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center py-2">
            <Pagination size="sm" className="mb-0">
              <Pagination.First onClick={() => setRunItemsPage(1)} disabled={runItemsPage === 1} />
              <Pagination.Prev onClick={() => setRunItemsPage((p) => Math.max(1, p - 1))} disabled={runItemsPage === 1} />
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(runItemsPage - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <Pagination.Item key={p} active={p === runItemsPage} onClick={() => setRunItemsPage(p)}>
                    {p}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next onClick={() => setRunItemsPage((p) => Math.min(totalPages, p + 1))} disabled={runItemsPage === totalPages} />
              <Pagination.Last onClick={() => setRunItemsPage(totalPages)} disabled={runItemsPage === totalPages} />
            </Pagination>
          </div>
        )}
      </div>
    );
  }

  function renderTasks() {
    return (
      <Container className="container pt-3 bg-white shadow-sm pb-3 mb-3 w-100">
        <h5 className="text-primary mb-1 fw-normal">Statement Runs</h5>
        <hr className="mt-1" />
        {selectedRunId ? renderRunDetail() : renderRunList()}
      </Container>
    );
  }

  function renderLeaveManagement() {
    return (
      <div>
        <UserAvailability user={user} />
      </div>
    );
  }

  function renderPasswordSecurity() {
    return (
      <Container className="container pt-3 bg-white shadow-sm pb-3 mb-3 w-100">
        <h5 className="text-primary mb-1 fw-normal">Password & Security</h5>
      </Container>
    );
  }

  function renderReleaseNotes() {
    return (
      <Container className="container pt-3 bg-white shadow-sm pb-3 mb-3 w-100">
        <h5 className="text-primary mb-1 fw-normal">Release Notes</h5>
        <hr className="mt-1" />
        <ReleaseNotesSection />
      </Container>
    );
  }

  function renderSupportRequests() {
    const PRIORITY_BADGES = { High: "danger", Medium: "warning", Low: "info" };
    const STATUS_BADGES_SUPPORT = {
      Open: "primary",
      "In Progress": "info",
      Resolved: "success",
      Closed: "secondary",
    };

    return (
      <Container className="container pt-3 bg-white shadow-sm pb-3 mb-3 w-100">
        <h5 className="text-primary mb-1 fw-normal">Support Requests</h5>
        <hr className="mt-1" />
        {supportLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        ) : supportRequests.length === 0 ? (
          <p className="text-muted text-center py-4">
            No support requests found.
          </p>
        ) : (
          <div className="table-responsive">
            <Table bordered hover responsive size="sm">
              <thead className="table-primary text-center">
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Screenshot</th>
                </tr>
              </thead>
              <tbody>
                {supportRequests.map((req) => (
                  <tr key={req._id}>
                    <td className="text-nowrap">
                      {new Date(req.createdAt).toLocaleDateString()}
                    </td>
                    <td
                      className="text-truncate"
                      style={{ maxWidth: 300 }}
                      title={req.description}
                    >
                      {req.description}
                    </td>
                    <td className="text-center">
                      <Badge
                        bg={PRIORITY_BADGES[req.priority] || "secondary"}
                        className={
                          req.priority === "Medium" ? "text-dark" : ""
                        }
                      >
                        {req.priority}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge
                        bg={
                          STATUS_BADGES_SUPPORT[req.status] || "secondary"
                        }
                      >
                        {req.status}
                      </Badge>
                    </td>
                    <td className="text-center">
                      {req.screenshotUrl ? (
                        <a
                          href={req.screenshotUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                        >
                          <i className="fa-light fa-image" />
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Container>
    );
  }

  return (
    <div>
      {/* Full-Width Title Bar with Margin at the Top */}
      <Container
        fluid
        className="d-flex w-100 bg-white shadow-sm p-3 mb-3 ms-3 me-3 justify-content-center"
        style={{ marginTop: "50px" }}
      >
        <h5 className="d-flex text-primary text-start fw-normal ms-1">
          {activeSection === "profile" && "PROFILE INFORMATION"}
          {activeSection === "tasks" && "SYSTEM TASKS"}
          {activeSection === "leave" && "SCHEDULE & AVAILABILITY"}
          {activeSection === "security" && "PASSWORD & SECURITY"}
          {activeSection === "releasenotes" && "RELEASE NOTES"}
        </h5>
      </Container>

      {/* Full-Width Layout Below */}
      <Container fluid>
        <Row className="w-100">
          {/* Sidebar Panel (Left-Aligned) */}
          <Col md={2}>
            <Container className="container pt-3 bg-white shadow-sm p-3 mb-3 w-100 vh-100">
              <div className="text-center mb-4">
                <img
                  src={
                    user.ppicURL ||
                    "https://practostorage.blob.core.windows.net/practotest/logo-placeholder-image.png?sp=r&st=2025-02-14T06:32:12Z&se=2025-02-14T14:32:12Z&spr=https&sv=2022-11-02&sr=b&sig=MFFyJLok0cf0Gzy60IkhlmnFLo5ItUHBcI86lL%2F%2BU5I%3D"
                  }
                  alt="Practice Logo"
                  height={80}
                />
                <Button
                  variant=""
                  className="position-absolute pt-4 ps-2"
                  onClick={() => handlePPicClick(user._id, user.ppicURL)}
                >
                  <i className="fas fa-edit text-primary"></i>
                </Button>
                <p className="text-primary mb-0">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-secondary mb-1">{user.occupation}</p>
                <hr />
              </div>
              <div className="text-center mb-3">
                <Button
                  variant={
                    activeSection === "profile" ? "primary" : "outline-primary"
                  }
                  className="w-100 mb-1"
                  onClick={() => setActiveSection("profile")}
                >
                  Profile Information
                </Button>
                <Button
                  variant={
                    activeSection === "tasks" ? "primary" : "outline-primary"
                  }
                  className="w-100 mb-1"
                  onClick={() => setActiveSection("tasks")}
                >
                  System Tasks
                </Button>
                <Button
                  variant={
                    activeSection === "leave" ? "primary" : "outline-primary"
                  }
                  className="w-100 mb-1"
                  onClick={() => setActiveSection("leave")}
                >
                  Availability
                </Button>
                <Button
                  variant={
                    activeSection === "releasenotes"
                      ? "primary"
                      : "outline-primary"
                  }
                  className="w-100 mb-1"
                  onClick={() => setActiveSection("releasenotes")}
                >
                  Release Notes
                </Button>
            {/*}    <Button
                  variant={
                    activeSection === "security" ? "primary" : "outline-primary"
                  }
                  className="w-100 mb-1"
                  onClick={() => setActiveSection("security")}
                >
                  Password & Security
                </Button> */}
              </div>
            </Container>
          </Col>

          {/* Main Content Area (Left-Aligned) */}
          <Col md={10}>
            <Container className="w-100">
              {activeSection === "profile" && renderProfileInformation()}
              {activeSection === "tasks" && renderTasks()}
              {activeSection === "support" && renderSupportRequests()}
              {activeSection === "leave" && renderLeaveManagement()}
              {activeSection === "security" && renderPasswordSecurity()}
              {activeSection === "releasenotes" && renderReleaseNotes()}
            </Container>
          </Col>
        </Row>
      </Container>
      {ppicModalShow && (
        <UserProfilePic
          show={ppicModalShow}
          onHide={() => setPpicModalShow(false)}
          userId={selectedUserId}
          ppicURL={selectedPPicURL}
          onPPicUpdate={handleRefresh}
        />
      )}
    </div>
  );
}
